import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { Prospect, ProspectPriority, ProspectStatus, DraftStockEntry } from '../types'
import { handleApiError } from '../utils/apiErrorHandler'
import { logger } from '../utils/logger'
import { ensureUserAgency } from '../utils/ensureAgency'
import { cascadeDelete, entityFilter, containsFilter } from '../utils/cascadeDeleteHelper'
import { removeFromWatchlist } from '../utils/watchlist'
import { linkProspectToEspn, espnIdOf } from '../utils/espnLink'

export type ProspectPayload = {
  full_name: string // Required
  headshot_url?: string | null
  email?: string | null
  phone?: string | null
  school?: string | null
  position?: string | null
  sport?: string | null
  league?: string | null
  date_of_birth?: string | null
  class_year?: string | null
  graduation_date?: string | null
  height?: string | null
  weight?: string | null
  gpa?: string | null
  status?: ProspectStatus | null
  priority?: ProspectPriority | null
  last_contact_at?: string | null
  // Recruiting Info (JSON fields)
  recruiting_rankings?: Record<string, any> | null
  scholarship_offers?: any[] | null
  visit_dates?: any[] | null
  commitment_status?: string | null
  committed_school?: string | null
  // Draft Projections
  projected_round?: number | null
  projected_pick?: number | null
  highlights_url?: string | null
  draft_stock_history?: DraftStockEntry[] | null
  // Performance & Stats (JSON fields)
  stats_summary?: Record<string, any> | null
  family_info?: Record<string, any> | null
  social_media?: Record<string, any> | null
  media_links?: Record<string, any> | null
  data_snapshots?: Record<string, any> | null
  tags?: string[] | null
  // Individual URL fields (PB dedicated columns)
  stats_url?: string | null
  instagram_url?: string | null
  twitter_url?: string | null
  tiktok_url?: string | null
  on3_url?: string | null
  rivals_url?: string | null
  pff_url?: string | null
  // Agency (auto-set on create)
  agency_id?: string
  owner_id?: string | null
}

function mapProspect(record: Record<string, any>): Prospect {
  return {
    id: record.id,
    full_name: record.full_name ?? '',
    headshot_url: record.headshot_url ?? null,
    email: record.email ?? null,
    phone: record.phone ?? null,
    school: record.school ?? null,
    position: record.position ?? null,
    sport: record.sport ?? null,
    league: record.league ?? null,
    date_of_birth: record.date_of_birth ?? null,
    class_year: record.class_year ?? null,
    graduation_date: record.graduation_date ?? null,
    height: record.height ?? null,
    weight: record.weight ?? null,
    gpa: record.gpa ?? null,
    status: record.status ?? null,
    priority: record.priority ?? null,
    last_contact_at: record.last_contact_at ?? null,
    recruiting_rankings: record.recruiting_rankings ?? null,
    scholarship_offers: Array.isArray(record.scholarship_offers) ? record.scholarship_offers : null,
    visit_dates: Array.isArray(record.visit_dates) ? record.visit_dates : null,
    commitment_status: record.commitment_status ?? null,
    committed_school: record.committed_school ?? null,
    projected_round: record.projected_round ?? null,
    projected_pick: record.projected_pick ?? null,
    highlights_url: record.highlights_url ?? null,
    draft_stock_history: Array.isArray(record.draft_stock_history) ? record.draft_stock_history : null,
    stats_summary: record.stats_summary ?? null,
    family_info: record.family_info ?? null,
    social_media: record.social_media ?? null,
    media_links: record.media_links ?? null,
    data_snapshots: record.data_snapshots ?? null,
    tags: Array.isArray(record.tags) ? record.tags : null,
    // Individual URL fields (PB dedicated columns)
    stats_url: record.stats_url ?? null,
    instagram_url: record.instagram_url ?? null,
    twitter_url: record.twitter_url ?? null,
    tiktok_url: record.tiktok_url ?? null,
    on3_url: record.on3_url ?? null,
    rivals_url: record.rivals_url ?? null,
    pff_url: record.pff_url ?? null,
    // AI-computed scores
    priority_score: record.priority_score ?? null,
    priority_updated_at: record.priority_updated_at ?? null,
    agency_id: record.agency_id ?? '',
    owner_id: record.owner_id ?? null,
    created: record.created ?? '',
    updated: record.updated ?? '',
    // Audit trail
    updated_by: record.updated_by ?? null,
    external_ids: record.external_ids && typeof record.external_ids === 'object' ? record.external_ids : null,
  }
}

export async function listProspects(options?: { filter?: string; limit?: number }) {
  try {
    // Get current user's agency for filtering
    const user = pb.authStore.model
    const agencyId = (user as any)?.agency_id
    const userId = user?.id
    const isSuperAdmin = (user as any)?.role === 'superadmin'

    // If no agency and not super admin and no user id, return empty
    if (!agencyId && !isSuperAdmin && !userId) {
      return []
    }

    // Build filter - super admins see all, regular users see their agency OR their prospects
    let baseFilter = ''
    if (!isSuperAdmin) {
      const conditions: string[] = []
      if (agencyId) {
        conditions.push(`agency_id = "${escapeFilterValue(agencyId)}"`)
      }
      if (userId) {
        conditions.push(`owner_id = "${escapeFilterValue(userId)}"`)
      }
      baseFilter = conditions.length > 0 ? `(${conditions.join(' || ')})` : ''
    }

    // Combine with any additional filters
    const combinedFilter = options?.filter
      ? (baseFilter ? `(${baseFilter}) && (${options.filter})` : options.filter)
      : baseFilter

    // Use pagination if limit is specified, otherwise fetch all
    let data: Record<string, any>[]
    const queryOptions: { filter?: string; sort: string } = { sort: '-updated' }
    if (combinedFilter) {
      queryOptions.filter = combinedFilter
    }

    if (options?.limit) {
      const result = await pb.collection('prospects').getList<Record<string, any>>(1, options.limit, queryOptions)
      data = result.items
    } else {
      data = await pb.collection('prospects').getFullList<Record<string, any>>({ ...queryOptions, batch: 500 })
    }
    return data.map(mapProspect)
  } catch (err: any) {
    // Autocancellation (component unmounted mid-request) is expected — return empty, not an error.
    const status = err?.status || err?.response?.status
    if (status === 0) {
      return []
    }
    // Any real failure must surface — never mask it as an empty prospect list.
    const apiError = handleApiError(err, 'List prospects')
    const errorToThrow = new Error(apiError.message || 'Failed to load prospects')
    ;(errorToThrow as any).status = apiError.status
    throw errorToThrow
  }
}

export type PaginatedProspectsResult = {
  items: Prospect[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}

export async function listProspectsPaginated(
  page: number = 1,
  perPage: number = 25,
  options?: { sort?: string; filter?: string }
): Promise<PaginatedProspectsResult> {
  try {
    // Get current user's agency for filtering
    const user = pb.authStore.model
    const agencyId = (user as any)?.agency_id
    const userId = user?.id
    const isSuperAdmin = (user as any)?.role === 'superadmin'

    if (!agencyId && !isSuperAdmin && !userId) {
      return { items: [], page: 1, perPage, totalItems: 0, totalPages: 0 }
    }

    // Build base filter - super admins see all, regular users see their agency OR their prospects
    let baseFilter = ''
    if (!isSuperAdmin) {
      const conditions: string[] = []
      if (agencyId) {
        conditions.push(`agency_id = "${escapeFilterValue(agencyId)}"`)
      }
      if (userId) {
        conditions.push(`owner_id = "${escapeFilterValue(userId)}"`)
      }
      baseFilter = conditions.length > 0 ? `(${conditions.join(' || ')})` : ''
    }

    // Combine with any additional filters
    const combinedFilter = options?.filter
      ? (baseFilter ? `(${baseFilter}) && (${options.filter})` : options.filter)
      : baseFilter

    const queryOptions: { filter?: string; sort?: string } = {}
    if (combinedFilter) {
      queryOptions.filter = combinedFilter
    }
    if (options?.sort) {
      queryOptions.sort = options.sort
    }

    const result = await pb.collection('prospects').getList<Record<string, any>>(page, perPage, queryOptions)
    return {
      items: result.items.map(mapProspect),
      page: result.page,
      perPage: result.perPage,
      totalItems: result.totalItems,
      totalPages: result.totalPages,
    }
  } catch (err: any) {
    // Autocancellation (component unmounted mid-request) is expected — return empty, not an error.
    const status = err?.status || err?.response?.status
    if (status === 0) {
      return { items: [], page: 1, perPage, totalItems: 0, totalPages: 0 }
    }
    // Any real failure (404/500/400/etc.) must surface — never mask it as "no prospects".
    const apiError = handleApiError(err, 'List prospects paginated')
    const errorToThrow = new Error(apiError.message || 'Failed to load prospects')
    ;(errorToThrow as any).status = apiError.status
    throw errorToThrow
  }
}

export async function getProspect(id: string) {
  const record = await pb.collection('prospects').getOne<Record<string, any>>(id)
  return mapProspect(record)
}

export async function createProspect(payload: ProspectPayload, ownerId?: string) {
  // Ensure user has an agency assigned (auto-create if needed)
  const userAgencyId = await ensureUserAgency()

  // Always set owner_id — use explicitly provided ownerId, or fall back to current auth user
  const effectiveOwnerId = ownerId || pb.authStore.model?.id

  const createPayload: Record<string, any> = {
    ...payload,
    agency_id: userAgencyId,
  }

  if (effectiveOwnerId) {
    createPayload.owner_id = effectiveOwnerId
  }

  // Remove any undefined/null/empty values before sending
  Object.keys(createPayload).forEach(key => {
    if (createPayload[key] === undefined || createPayload[key] === null || createPayload[key] === '') {
      delete createPayload[key]
    }
  })

  try {
    const record = await pb.collection('prospects').create(createPayload)
    const prospect = mapProspect(record)

    // Link to ESPN (id, headshot, school) in the background. Does not block
    // the return; the page shows the outcome on its ESPN line. A failure
    // here is logged, never hidden as "no match".
    linkProspectToEspn(prospect).catch(err => logger.error('ESPN link on prospect create failed', prospect.id, err))

    return prospect
  } catch (err: any) {
    const apiError = handleApiError(err, 'Prospect creation')
    const errorToThrow = new Error(apiError.message)
    ;(errorToThrow as any).status = apiError.status
    ;(errorToThrow as any).fieldErrors = apiError.fieldErrors
    throw errorToThrow
  }
}

export async function updateProspect(
  prospectId: string,
  payload: Partial<ProspectPayload>,
) {
  try {
    // Set updated_by for audit trail
    const user = pb.authStore.model
    const updatePayload: Record<string, any> = { ...payload }
    if (user?.id) {
      updatePayload.updated_by = user.id
    }

    const record = await pb.collection('prospects').update(prospectId, updatePayload)
    const prospect = mapProspect(record)
    // A new school or league is a new chance to place them on ESPN. Only
    // when no id is on the record: a person's link is never replaced here.
    if (('school' in payload || 'league' in payload) && !espnIdOf(prospect)) {
      linkProspectToEspn(prospect).catch(err => logger.error('ESPN link on prospect update failed', prospect.id, err))
    }
    return prospect
  } catch (err: any) {
    const apiError = handleApiError(err, 'Update prospect')
    const errorToThrow = new Error(apiError.message)
    ;(errorToThrow as any).status = apiError.status
    throw errorToThrow
  }
}

export async function deleteProspect(prospectId: string) {
  return deleteProspectCascade(prospectId)
}

/**
 * Delete prospect and related records (best-effort cascade).
 * Fetches all related collections in parallel, deletes in batched parallel.
 */
export async function deleteProspectCascade(prospectId: string) {
  try {
    logger.info('Starting cascade delete for prospect:', prospectId)

    // Parent first: a refused delete changes nothing else.
    await pb.collection('prospects').delete(prospectId)
    // Off this person's watchlist. Other people's lists keep the id; it is
    // filtered out at read time against the prospects that exist.
    removeFromWatchlist(prospectId)

    await cascadeDelete([
      { collection: 'tasks',           filter: entityFilter('entity_type', 'prospect', 'entity_id', prospectId) },
      { collection: 'notes',           filter: entityFilter('related_entity_type', 'prospect', 'related_entity_id', prospectId) },
      { collection: 'calendar_events', filter: entityFilter('related_entity_type', 'prospect', 'related_entity_id', prospectId) },
      // Birthday rows are written by the schedule job and keyed only by
      // external_id; the entity fields above never see them.
      { collection: 'calendar_events', filter: containsFilter('external_id', `birthday:prospect:${prospectId}:`) },
    ])

    // College fixtures shared with others at the school name the prospect in
    // prospect_ids. Take them off; a row left with nobody on it goes too.
    const shared = await pb.collection('calendar_events').getFullList<Record<string, any>>({
      filter: containsFilter('prospect_ids', prospectId),
      fields: 'id,client_ids,prospect_ids',
    })
    for (const row of shared) {
      const rest = (row.prospect_ids as string[]).filter(id => id !== prospectId)
      const clientsLeft = Array.isArray(row.client_ids) ? row.client_ids.length : 0
      if (rest.length === 0 && clientsLeft === 0) await pb.collection('calendar_events').delete(row.id)
      else await pb.collection('calendar_events').update(row.id, { prospect_ids: rest })
    }
    logger.info('Prospect deleted successfully')
  } catch (err: any) {
    const apiError = handleApiError(err, 'Delete prospect cascade')
    const errorToThrow = new Error(apiError.message)
    ;(errorToThrow as any).status = apiError.status
    throw errorToThrow
  }
}

/**
 * Update data snapshots for a prospect
 */
export async function updateProspectDataSnapshots(
  prospectId: string,
  snapshots: Record<string, any>
) {
  // Merge, not replace — same reason as updateClientDataSnapshots.
  const current = await pb.collection('prospects').getOne(prospectId)
  const existing = (current.data_snapshots ?? {}) as Record<string, any>
  const record = await pb.collection('prospects').update(prospectId, {
    data_snapshots: { ...existing, ...snapshots },
  })
  return mapProspect(record)
}

export function groupProspectsByStatus(prospects: Prospect[]) {
  const grouped: Record<string, Prospect[]> = {}
  prospects.forEach(prospect => {
    const status = prospect.status ?? 'unknown'
    if (!grouped[status]) {
      grouped[status] = []
    }
    grouped[status].push(prospect)
  })
  return grouped
}
